import {
  BinIcon,
  Button,
  CalendarAddIcon,
  DateInput,
  ErrorMessage,
  Input,
  Select,
  TimeInput,
} from "@jutro/ui";
import { OptionWithSideElements } from "@jutro/ui/dist/Select/tools";
import { Dayjs } from "dayjs";
import { useRef } from "react";
import { Controller } from "react-hook-form";
import { EdmNonWorkingPeriod } from "lib/graphql/megaSchema";
import { useNonWorkingPeriodModalForm } from "views/StaffSchedule/StaffScheduleCreator/hooks/useNonWorkingPeriodModalForm";
import {
  StaffOption,
  leaveTypeOptions,
} from "views/StaffSchedule/StaffScheduleCreator/tools";

type Props = {
  staffOptions: OptionWithSideElements<StaffOption>[];
  isEditMode: boolean;
  selectedNonWorkingPeriod: EdmNonWorkingPeriod | undefined;
  isCreateMode: boolean;
  selectedBlockStaffId: string;
  startDate: Dayjs;
  selectedDay?: Dayjs;
  onCloseModal: () => void;
};

export const NonWorkingPeriodActionsModal = ({
  staffOptions,
  selectedNonWorkingPeriod,
  isCreateMode,
  isEditMode,
  selectedBlockStaffId,
  startDate,
  selectedDay,
  onCloseModal,
}: Props) => {
  const {
    control,
    errors,
    createNonWorkingPeriodLoading,
    deleteNonWorkingPeriodLoading,
    handleSubmit,
    onSubmit,
  } = useNonWorkingPeriodModalForm({
    selectedNonWorkingPeriod,
    startDate,
    selectedDay,
    staffOptions,
    selectedBlockStaffId,
    onCloseModal,
  });

  const timeFromInputRef = useRef<HTMLInputElement>(null);
  const timeToInputRef = useRef<HTMLInputElement>(null);

  const { timeTo: timeToFieldError, timeFrom: timeFromFieldError } = errors;

  return (
    <form className="flex flex-col gap-4">
      <div className="flex gap-4">
        <Controller
          control={control}
          name="staff"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Select
              label="Przypisz do*"
              placeholder="Wybierz z listy"
              isSearchable={isCreateMode || !selectedBlockStaffId}
              disabled={Boolean(selectedBlockStaffId) || isEditMode}
              options={staffOptions}
              value={value}
              onChange={onChange}
              error={error?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="leaveReason"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Select
              label="Powód*"
              placeholder="Wybierz z listy"
              isSearchable={false}
              disabled={isEditMode}
              options={leaveTypeOptions}
              value={value}
              onChange={onChange}
              error={error?.message}
            />
          )}
        />
      </div>

      <div className="flex items-start gap-4">
        <Controller
          control={control}
          name="day"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <DateInput
              label="Data*"
              format="yyyy-MM-dd"
              maxDate={startDate.endOf("month").toDate()}
              minDate={startDate.startOf("month").toDate()}
              clearIcon={null}
              dayPlaceholder="DD"
              monthPlaceholder="MM"
              yearPlaceholder="RRRR"
              calendarIcon={<CalendarAddIcon size="sm" />}
              locale="pl"
              value={value}
              disabled={isEditMode}
              onChange={onChange}
              error={error?.message}
            />
          )}
        />

        <div className="flex flex-col gap-1">
          <span className="font-label">Przedział czasowy*</span>
          <div className="flex gap-2">
            <Controller
              control={control}
              name="timeFrom"
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <TimeInput
                  ref={timeFromInputRef}
                  disabled={isEditMode}
                  error={Boolean(timeToFieldError || error)}
                  minutesStep={5}
                  value={value}
                  onChange={onChange}
                  onComplete={() => timeToInputRef.current?.focus()}
                />
              )}
            />

            <Controller
              control={control}
              name="timeTo"
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <TimeInput
                  ref={timeToInputRef}
                  disabled={isEditMode}
                  error={Boolean(timeToFieldError || error)}
                  minutesStep={5}
                  value={value}
                  onChange={onChange}
                  onBackspaceAtEmpty={() => timeFromInputRef.current?.focus()}
                />
              )}
            />
          </div>
          {Boolean(timeToFieldError || timeFromFieldError) && (
            <ErrorMessage
              error={timeToFieldError?.message || timeFromFieldError?.message}
            />
          )}
        </div>
      </div>

      <Controller
        control={control}
        name="comment"
        render={({ field: { onChange, value } }) => (
          <Input
            label="Komentarz"
            placeholder="np. szczegóły dotyczące urlopu lub powodu nadgodzin"
            disabled={isEditMode}
            value={value ?? ""}
            onChange={onChange}
          />
        )}
      />

      <div className="flex justify-between pt-8">
        {isEditMode && (
          <div className="flex justify-start">
            <Button
              text="Usuń"
              variant="negative"
              icon={<BinIcon />}
              full={false}
              type="button"
              loading={
                deleteNonWorkingPeriodLoading || createNonWorkingPeriodLoading
              }
              onClick={handleSubmit((data) => onSubmit(data, "delete"))}
            />
          </div>
        )}

        <div className="flex flex-1 justify-end gap-2">
          <Button
            text="Anuluj"
            variant="ghost"
            full={false}
            type="button"
            onClick={onCloseModal}
          />
          <Button
            text="Utwórz"
            full={false}
            type="button"
            loading={
              createNonWorkingPeriodLoading || deleteNonWorkingPeriodLoading
            }
            onClick={handleSubmit((data) => onSubmit(data, "create"))}
          />
        </div>
      </div>
    </form>
  );
};
